import type { ApiError, Parcel } from "@/shared/types";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  LoadUpdatesPayload,
  LoadUpdatesSuccessPayload,
  ParcelDetailState,
} from "./types";

/**
 * Only the newest post is rendered in full — the rest are one-line archive
 * rows — so a page holds far more than it used to. Five made "load older"
 * appear on almost every parcel; twelve covers a whole season for most.
 */
export const UPDATES_PAGE_SIZE = 12;

const initialState: ParcelDetailState = {
  vaultId: null,
  parcel: null,
  isLoading: true,
  error: null,
  updates: [],
  updatesPage: 1,
  updatesTotalPages: 1,
  updatesTotalRecords: 0,
  isUpdatesLoading: true,
  isUpdatesLoadingMore: false,
  updatesError: null,
};

export const parcelDetailSlice = createSlice({
  name: "parcelDetail",
  initialState,
  reducers: {
    /**
     * Saga trigger for the whole screen — the saga fetches the parcel and then
     * chains the first page of updates, so the container dispatches once.
     *
     * Navigating from one parcel to another clears everything: showing the
     * previous parcel's photographs under a new name for a frame is worse than
     * showing a skeleton.
     */
    loadParcel: (state, action: PayloadAction<string>) => {
      const changed = state.vaultId !== action.payload;
      state.vaultId = action.payload;
      state.isLoading = true;
      state.error = null;
      if (changed) {
        state.parcel = null;
        state.updates = [];
        state.updatesPage = 1;
        state.updatesTotalPages = 1;
        state.updatesTotalRecords = 0;
        state.isUpdatesLoading = true;
        state.updatesError = null;
      }
    },
    loadParcelSuccess: (state, action: PayloadAction<Parcel>) => {
      state.parcel = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    loadParcelFailure: (state, action: PayloadAction<ApiError>) => {
      state.parcel = null;
      state.isLoading = false;
      state.isUpdatesLoading = false;
      state.error = action.payload;
    },

    loadUpdates: (state, action: PayloadAction<LoadUpdatesPayload>) => {
      state.updatesError = null;
      if (action.payload.page <= 1) {
        state.isUpdatesLoading = true;
      } else {
        state.isUpdatesLoadingMore = true;
      }
    },
    loadUpdatesSuccess: (
      state,
      action: PayloadAction<LoadUpdatesSuccessPayload>
    ) => {
      const { data, append } = action.payload;
      state.updates = append ? [...state.updates, ...data.content] : data.content;
      state.updatesPage = data.page;
      state.updatesTotalPages = data.totalPages;
      state.updatesTotalRecords = data.totalRecords;
      state.isUpdatesLoading = false;
      state.isUpdatesLoadingMore = false;
      state.updatesError = null;
    },
    loadUpdatesFailure: (state, action: PayloadAction<ApiError>) => {
      state.isUpdatesLoading = false;
      state.isUpdatesLoadingMore = false;
      state.updatesError = action.payload;
    },
  },
});

export const { actions: parcelDetailActions, reducer: parcelDetailReducer } =
  parcelDetailSlice;
